import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';

import { formatCountdown, getCountdownTarget } from '../utils';
import prizePropType from './prizePropType';

// The server flips a prize's status once its time has passed, so refresh just after.
const ELAPSED_GRACE_MS = 1000;
// setTimeout fires at once for longer delays, so further targets are left alone.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * A live clock to the active prize closing, or to the next prize opening. Renders
 * nothing once the last prize has closed.
 */
export default function PrizeCountdown({ prizes, onElapsed }) {
  const target = useMemo(() => getCountdownTarget(prizes), [prizes]);
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    if (!target) return undefined;

    const interval = setInterval(() => setNow(Date.now()), 1000);
    const delay = Math.max(0, target.at - Date.now()) + ELAPSED_GRACE_MS;
    const timeout = delay <= MAX_TIMEOUT_MS && setTimeout(onElapsed, delay);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [target, onElapsed]);

  if (!target) return null;

  const label =
    target.kind === 'closes'
      ? t`Today's prize closes in`
      : t`Next prize unlocks in`;

  return (
    // A timer isn't announced on every tick, only when it's read.
    <div
      role="timer"
      aria-label={label}
      className="inline-grid gap-1.5 rounded-xl bg-black px-5 py-4 text-white"
    >
      <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray">
        <span
          className="h-2 w-2 shrink-0 rounded-full bg-green motion-safe:animate-pulse"
          aria-hidden="true"
        />
        {label}
      </span>
      <span className="text-[32px] font-extrabold tabular-nums leading-none md:text-[40px]">
        {formatCountdown(target.at - now)}
      </span>
    </div>
  );
}

PrizeCountdown.propTypes = {
  prizes: PropTypes.arrayOf(prizePropType).isRequired,
  onElapsed: PropTypes.func.isRequired
};
